import parse from "html-react-parser"
import type { ReactNode } from "react"

import iconCheck from "images/icons/checkmark.svg?raw"
import iconClose from "images/icons/close.svg?raw"

import type { UIAction } from "../types"
import styles from "./card_shell.module.scss"

// Resolves the label for a server-provided action so it can ride along as the user turn when the
// action is forwarded to the agent. Falls back when the server omits the action.
export function actionLabel(actions: UIAction[] | undefined, id: string, fallback: string): string {
  return actions?.find(action => action.id === id)?.label || fallback
}

interface CardShellProps {
  // Omitted once the card is settled — a card committed to the chat drops its header.
  prompt?: string
  promptSize?: "md" | "lg"
  // Omitted once the card is settled — an answered card has no close control.
  onDismiss?: () => void
  children: ReactNode
}

export const CardShell = ({ prompt, promptSize = "md", onDismiss, children }: CardShellProps) => (
  <div className={styles["row"]}>
    <div className={styles["card"]}>
      {prompt || onDismiss ? (
        <div className={styles["header"]}>
          <span className={`${styles["prompt"]} ${styles[promptSize]}`}>{prompt}</span>
          {onDismiss ? (
            <button type="button" aria-label="Dismiss" className={styles["dismiss"]} onClick={onDismiss}>
              {parse(iconClose)}
            </button>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  </div>
)

// The disabled control shown on a settled permission card in place of its live actions. `confirmed`
// adds a checkmark for affirmative choices (e.g. "Upgraded to Pro ✓", "Connected Gmail ✓").
export const CardSettledAction = ({ label, confirmed }: { label: string; confirmed?: boolean }) => (
  <div className={styles["actions"]}>
    <button type="button" className={styles["settled"]} disabled>
      <span>{label}</span>
      {confirmed ? (
        <span className={styles["settled-check"]} aria-hidden="true">
          {parse(iconCheck)}
        </span>
      ) : null}
    </button>
  </div>
)

export const CardFallback = ({ text }: { text: string }) => (
  <div className={styles["row"]}>
    <div className={styles["fallback"]}>{text}</div>
  </div>
)

export const CardActions = ({ children }: { children: ReactNode }) => (
  <div className={styles["actions"]}>{children}</div>
)

interface CardButtonProps {
  variant: "primary" | "secondary"
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}

export const CardButton = ({ variant, disabled, onClick, children }: CardButtonProps) => (
  <button type="button" className={styles[variant]} disabled={disabled} onClick={onClick}>
    {children}
  </button>
)
